import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface SchoolPackage {
  id: string
  name: string
  price: number | null
  currency: string
  inCarHours: number
  perks: string[]
  isActive: boolean
}

interface RpcResult {
  ok: boolean
  code?: string
}

/**
 * A school's defined lesson packages (migration 092) — e.g. "Master Package 2", $730,
 * 11 in-car hours. Write path is plain RLS (no RPC) since a package is simple admin-owned
 * config with no cross-table derivation — the same shape migration 008 used for
 * "schools: owner/admin update" itself. Read is open to any active member of the school
 * (an instructor needs to see package names/hours, not just the admin).
 */
export function useSchoolPackages(schoolId: string | null) {
  const [packages, setPackages] = useState<SchoolPackage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const fetchAll = useCallback(async () => {
    if (!schoolId) { setPackages([]); setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const { data, error: fetchErr } = await supabase
        .from('school_packages')
        .select('id, name, price, currency, in_car_hours, perks, is_active')
        .eq('school_id', schoolId)
        .order('in_car_hours', { ascending: true })

      if (fetchErr) { setError('Could not load packages.'); return }
      setPackages((data ?? []).map(p => ({
        id: p.id,
        name: p.name,
        price: p.price === null || p.price === undefined ? null : Number(p.price),
        currency: p.currency,
        inCarHours: Number(p.in_car_hours),
        perks: p.perks ?? [],
        isActive: p.is_active,
      })))
    } finally {
      setLoading(false)
    }
  }, [schoolId])

  useEffect(() => { fetchAll() }, [fetchAll])

  const createPackage = useCallback(async (fields: {
    name: string; price: number | null; inCarHours: number; perks: string[]
  }): Promise<RpcResult> => {
    if (!schoolId || saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { error: insertErr } = await supabase.from('school_packages').insert({
        school_id: schoolId,
        name: fields.name,
        price: fields.price,
        in_car_hours: fields.inCarHours,
        perks: fields.perks.length > 0 ? fields.perks : null,
      })
      if (insertErr) return { ok: false, code: 'internal_error' }
      await fetchAll()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [schoolId, saving, fetchAll])

  const setPackageActive = useCallback(async (packageId: string, isActive: boolean): Promise<RpcResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { error: updateErr } = await supabase.from('school_packages')
        .update({ is_active: isActive })
        .eq('id', packageId)
      if (updateErr) return { ok: false, code: 'internal_error' }
      await fetchAll()
      return { ok: true }
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  return { packages, loading, error, saving, createPackage, setPackageActive, refresh: fetchAll }
}
